import noop from 'utils/noop';
import { createContext, useContext } from 'react';
import { ApolloError } from '@apollo/client';
import { ListAction } from 'modules/campaigns/editor/types';
import { ListType, LIST_TYPES } from 'constants/listTypes';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { PaginationUpdateEvent } from 'ui/paginationFooter';
import { AudienceList } from 'modules/domains/campaign/types';
import {
  GraphQLAudienceList,
  QueryAudienceListsInfo,
  QueryAudienceListsSort,
} from '../../../hooks';
import { TABLE_HEADER_COL_VALUES } from '../components/Table';
import { ListCreatorNamesById } from '../hooks';
import {
  CALCULATE_SELECTED_LISTS_SIZE_DEFAULT_STATE,
  CalculateSelectedListsSizesState,
} from '../hooks/useCalculateSelectedListsSizes';

export interface AudienceContextState {
  lists: GraphQLAudienceList[];
  listCreatorNames: ListCreatorNamesById;
  listTypes: ListType[];
  selectedSendLists: AudienceList[];
  selectedSuppressionLists: AudienceList[];
  selectedListsCount: number;
  searchText: string;
  sort: QueryAudienceListsSort;
  pagination: Omit<QueryAudienceListsInfo, '__typename'>;
  isLoading: boolean;
  hasError: boolean;
  errorMessage?: ApolloError;
  selectedSendListsSizes: CalculateSelectedListsSizesState;
  selectedSuppressionListsSizes: CalculateSelectedListsSizesState;
  refetch: () => void;
  handlePageChange: (event: PaginationUpdateEvent) => void;
  handleListTypeChange: (listType: string) => void;
  handleSort: (columnValue: string) => void;
  handleSearch: (searchText: string) => void;
  toggleListSelection: (listAction: ListAction, list: AudienceList) => void;
  clearSelections: () => void;
}

export const DEFAULT_LIST_TYPES = Object.values(LIST_TYPES);

export const DEFAULT_PAGINATION_INFO = {
  count: 0,
  limit: 10,
  offset: 0,
  page: 1,
};

export const DEFAULT_SORT: QueryAudienceListsSort = {
  sortBy: TABLE_HEADER_COL_VALUES.UpdatedAt,
  sortDirection: SORT_DIRECTIONS.DESCENDING,
};

export const DEFAULT_AUDIENCE_CONTEXT_STATE: AudienceContextState = {
  lists: [],
  listCreatorNames: {},
  listTypes: DEFAULT_LIST_TYPES,
  selectedSendLists: [],
  selectedSuppressionLists: [],
  selectedListsCount: 0,
  sort: DEFAULT_SORT,
  searchText: '',
  pagination: DEFAULT_PAGINATION_INFO,
  isLoading: true,
  hasError: false,
  errorMessage: undefined,
  selectedSendListsSizes: CALCULATE_SELECTED_LISTS_SIZE_DEFAULT_STATE,
  selectedSuppressionListsSizes: CALCULATE_SELECTED_LISTS_SIZE_DEFAULT_STATE,
  refetch: noop,
  handlePageChange: noop,
  handleListTypeChange: noop,
  handleSort: noop,
  handleSearch: noop,
  toggleListSelection: noop,
  clearSelections: noop,
};

export const AudienceContext = createContext<AudienceContextState>(DEFAULT_AUDIENCE_CONTEXT_STATE);
export const useAudienceContext = () => useContext(AudienceContext);
